import React, { useEffect, useRef } from 'react';
import { ArrowDown, ArrowUp, ImagePlus, Trash2 } from 'lucide-react';
import Thumb from './Thumb';
import { guessImageMime, isValidURL } from '../utils/helpers';

let keySeed = 0;
export const newMediaKey = () => {
  keySeed += 1;
  return `media-${Date.now().toString(36)}-${keySeed}`;
};

/**
 * Ordered list of product images (URL only: there is no upload endpoint).
 * value: [{ _key, title, url, mimeType, fileSizeInBytes }]. The first image is the cover.
 * Use as a form control: <Field name="media">{f => <MediaEditor value={f.value} onChange={f.onChange} id={f.id} />}</Field>
 */
const MediaEditor = ({ value, onChange, id }) => {
  const items = Array.isArray(value) ? value : [];
  const focusKey = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!focusKey.current || !listRef.current) return;
    const input = listRef.current.querySelector(`[data-media-url="${focusKey.current}"]`);
    focusKey.current = null;
    input?.focus();
  });

  const update = (index, patch) => onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    onChange(next);
  };

  const remove = index => onChange(items.filter((_, i) => i !== index));

  const add = () => {
    const key = newMediaKey();
    focusKey.current = key;
    onChange([...items, { _key: key, title: '', url: '', mimeType: '', fileSizeInBytes: 0 }]);
  };

  return (
    <div id={id} tabIndex={-1}>
      {items.length === 0 ? (
        <p className="adm-note" style={{ margin: '0 0 12px' }}>
          No images yet. The store shows a plain tile until you add one.
        </p>
      ) : (
        <ol className="adm-media-list" ref={listRef}>
          {items.map((item, index) => {
            const url = (item.url || '').trim();
            const invalid = url !== '' && !isValidURL(url);
            const n = index + 1;
            const key = item._key || `row-${index}`;
            return (
              <li key={key} className="adm-media-row">
                <Thumb src={invalid ? '' : url} variant="lg" badge={index === 0 ? 'Cover' : undefined} />
                <div className="adm-media-row__fields">
                  <input
                    className="pk-input"
                    aria-label={`Image ${n} title`}
                    placeholder="Title (optional)"
                    value={item.title || ''}
                    maxLength={120}
                    onChange={e => update(index, { title: e.target.value })}
                  />
                  <input
                    className="pk-input"
                    type="url"
                    inputMode="url"
                    data-media-url={key}
                    aria-label={`Image ${n} URL`}
                    aria-invalid={invalid || undefined}
                    placeholder="https://"
                    value={item.url || ''}
                    onChange={e =>
                      update(index, {
                        url: e.target.value,
                        mimeType: guessImageMime(e.target.value),
                        fileSizeInBytes: 0,
                      })
                    }
                  />
                </div>
                <div className="adm-media-row__tools">
                  <button
                    type="button"
                    className="adm-icon-btn"
                    aria-label={`Move image ${n} up`}
                    title="Move up"
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUp aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="adm-icon-btn"
                    aria-label={`Move image ${n} down`}
                    title="Move down"
                    disabled={index === items.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDown aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="adm-icon-btn"
                    data-tone="danger"
                    aria-label={`Remove image ${n}`}
                    title="Remove"
                    onClick={() => remove(index)}
                  >
                    <Trash2 aria-hidden="true" />
                  </button>
                </div>
                {invalid && <div className="pk-error">Enter a full image URL that starts with https://</div>}
              </li>
            );
          })}
        </ol>
      )}
      <button type="button" className="pk-btn pk-btn--secondary pk-btn--sm" onClick={add}>
        <ImagePlus aria-hidden="true" />
        Add image
      </button>
    </div>
  );
};

/** Form rule: every image that has a URL must have a valid one (empty rows are dropped on save). */
export const mediaRule = {
  validator: (_, value) => {
    const items = Array.isArray(value) ? value : [];
    const bad = items.findIndex(item => {
      const url = (item?.url || '').trim();
      return url !== '' && !isValidURL(url);
    });
    return bad === -1 ? Promise.resolve() : Promise.reject(new Error(`Image ${bad + 1} needs a valid URL.`));
  },
};

export default MediaEditor;
